// src/utils/commentTemplates.js
//
// Pure helpers for the OTTI comment-template editor. Priority is derived purely
// from row order (the user drags rows to reorder), so these helpers never carry
// an explicit priority through the UI — they renumber on the way to the server.

export const TEMPLATE_FIELDS = ['SEC_OWNER', 'STYPEL3', 'OTTI_FLG', 'COMMENT_TEXT'];

// Selectable OTTI_FLG values for the editor dropdown, ordered alphabetically
// ('' sorts first = the wildcard / "(any)" option). The non-blank values mirror
// the domain produced by computeRowFlags() in ottiUtils — keep them in sync.
export const OTTI_FLG_OPTIONS = [
  '',
  'No OTTI',
  'Possible GAAP OTTI',
  'Possible OTTI',
  'Possible STAT OTTI',
];

// A fresh blank rule for the "Add Template" action.
export const blankTemplate = () => ({
  SEC_OWNER: '',
  STYPEL3: '',
  OTTI_FLG: '',
  COMMENT_TEXT: '',
});

const blank = (v) => String(v ?? '').trim() === '';

// Every rule needs a comment (the match columns may be blank = wildcard).
// Returns an error message for the first offending row, or null when valid.
export const validateTemplates = (rows) => {
  if (!Array.isArray(rows)) return 'No templates to save.';
  const idx = rows.findIndex((r) => blank(r.COMMENT_TEXT));
  return idx === -1 ? null : `Template ${idx + 1} needs a comment.`;
};

// Build the server payload from the rows in display order. Priority follows the
// order (1, 2, 3, ...) — the user reorders by dragging, never types numbers;
// blank match columns become null (wildcard); strings are trimmed.
export const buildTemplatePayload = (rows) =>
  (rows || []).map((r, i) => ({
    PRIORITY: i + 1,
    SEC_OWNER: blank(r.SEC_OWNER) ? null : String(r.SEC_OWNER).trim(),
    STYPEL3: blank(r.STYPEL3) ? null : String(r.STYPEL3).trim(),
    OTTI_FLG: blank(r.OTTI_FLG) ? null : String(r.OTTI_FLG).trim(),
    COMMENT_TEXT: String(r.COMMENT_TEXT ?? '').trim(),
  }));

// Canonical key for change-detection: two row sets are "equal" iff the save
// they'd produce is identical. Built from the payload, so it's insensitive to
// internal ids and to whitespace/wildcard normalization, but sensitive to order
// (priority follows order) — reordering and then restoring the original order
// yields the same key, so the Save button correctly goes back to disabled.
export const templatesKey = (rows) => JSON.stringify(buildTemplatePayload(rows));

// Normalize rows coming back from the server (null match columns -> '' so the
// grid editors show empty cells rather than the string "null").
export const normalizeTemplateRows = (rows) =>
  (rows || []).map((r) => ({
    SEC_OWNER: r.SEC_OWNER ?? '',
    STYPEL3: r.STYPEL3 ?? '',
    OTTI_FLG: r.OTTI_FLG ?? '',
    COMMENT_TEXT: r.COMMENT_TEXT ?? '',
  }));

// The {PRIORITY, MODIFIED_DATE} snapshot of the rules as the server returned
// them. The save sends it back unchanged: the writer checks each rule's
// MODIFIED_DATE, so a save by someone else since the load is a 409.
export const loadedSnapshot = (rows) =>
  (rows || []).map((r) => ({
    PRIORITY: r.PRIORITY,
    MODIFIED_DATE: typeof r.MODIFIED_DATE === 'string' && r.MODIFIED_DATE !== '' ? r.MODIFIED_DATE : null,
  }));

// Move an item within an array (used to mirror an AG Grid drag into React state).
export const moveRow = (rows, fromIndex, toIndex) => {
  const next = [...rows];
  if (
    fromIndex < 0 || fromIndex >= next.length ||
    toIndex < 0 || toIndex >= next.length ||
    fromIndex === toIndex
  ) {
    return next;
  }
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
};
